/**
 * Dev-only **Feature toggles** — force a web feature flag On or Off in THIS browser, the web port of the
 * native apps' Dev Tools ▸ Feature Toggles screen (`ios/.../FeatureTogglesScreen.swift`,
 * `android/.../FeatureToggles.kt`).
 *
 * Same model as native, deliberately:
 *
 *   - Every toggle the app conducts is listed (`WEB_TOGGLES`), with its **natural** conducted state
 *     shown on the Default pill ("Default · On" / "Default · Off").
 *   - Per toggle a **tri-state**: Default (no override) · On (`"true"`) · Off (`"false"`).
 *   - A **Persist** switch (default ON) deciding whether the override survives a reload — native's
 *     "survives a relaunch". Persisted overrides go to `localStorage`; session-only ones to
 *     `sessionStorage`, which dies with the tab exactly as native's in-memory override dies with the
 *     process.
 *
 * The flag read-path consults `toggleOverride()` FIRST (see `lib/instance-auth-flag.ts`), so a forced
 * value behaves like the real conducted one everywhere downstream — no special-casing per call site.
 *
 * Every entry point is gated on `devToolsUnlocked()`, the same gate as the mock layer: a real user's
 * browser holds no grant, so a key left in storage (or hand-written) does nothing at all.
 */

import { devToolsUnlocked } from './dev-tools';

/** A forced value. Kept as the string native uses, so the two stores read identically. */
export type ToggleOverride = 'true' | 'false';

export interface WebToggle {
  /** The Petri/Conductor spec id — what the server conducts. */
  spec: string;
  /** What it gates, in one line, for the panel. */
  description: string;
}

/** The signed-instance auth gate. Exported so the flag's own module reads the same literal. */
export const INSTANCE_AUTH_SPEC = 'symphonyInstanceAuthWeb';

/** The live voice-call gate. Exported so the flag's own module reads the same literal. */
export const VOICE_CALL_SPEC = 'symphonyVoiceCallWeb';

/** The Credit Miner in the image placeholder. Exported so the flag's own module reads the same literal. */
export const CREDIT_MINER_SPEC = 'symphonyCreditMinerWeb';

/**
 * The out-of-credits "earn credits instead" offer. Exported so the flag's own module reads the same
 * literal — and the NATIVE apps conduct this exact spec id, so the three surfaces ramp together.
 */
export const EARN_CREDITS_OFFER_SPEC = 'symphonyOutOfCreditsEarnOfferEnabled';

/**
 * The package picker's billing-cycle-step arm.
 *
 * **Deliberately NOT `…Web` like its three siblings above.** This is the key Petri actually holds
 * (`fetchExperimentById 5209969`), and `conduct` matches the string exactly — so the panel has to
 * name the same one the server asks for. It read `symphonyBillingCycleStepWeb` while Petri held
 * `specs.orion.SymphonyBillingCycleStep`, which is what left an allowlist of three named users on
 * the old picker with the experiment looking correctly configured.
 *
 * The `orion` in it is a live identifier, not prose, so the rename does not touch it (CLAUDE.md).
 *
 * It genuinely is the only surface that conducts this — but NOT because the phones have a picker of
 * their own. iOS and Android both send buyers to this exact page (`CreditsUpgrade.symphonyURL` ->
 * symphony.wix.com/packages), so all three surfaces are already served by this one gate.
 */
export const BILLING_CYCLE_STEP_SPEC = 'specs.orion.SymphonyBillingCycleStep';

/**
 * The daily-limit bar's rollout gate — the second meter reading how much of today's allowance is
 * spent (Figma 15375-104311).
 *
 * **This is the CAP's own key, not a web arm of its own**, so the bar rolls out to exactly the
 * population the ceiling applies to rather than ramping out of step with it. Hence bare camelCase
 * with no `…Web` suffix and no `specs.` prefix — do not "correct" it to either shape.
 *
 * ⚠️ Which object answers this key has been read wrong once — see the route for the evidence and for
 * why "there are two objects" is NOT the settled conclusion it was first written as. Survivable either
 * way, because the bar also requires the BFF to have sent the numbers.
 *
 * Forcing this On in the panel does NOT conjure a bar on an account the BFF reports no ceiling for —
 * the data is the other half of the gate, and it is the half that cannot be overridden.
 */
export const DAILY_LIMIT_BAR_SPEC = 'symphonyDailyCreditsLimit';

/**
 * "Change payment method" on the current-plan card's ⋯ menu.
 *
 * Not a `specs.*` Petri spec and not a Conductor Release — this one ramps on a databag percentage
 * alone (`pages/api/flags/change-payment-method.ts` says why), so the name exists only to key this
 * override and the server's bucket salt.
 */
export const CHANGE_PAYMENT_METHOD_SPEC = 'symphonyChangePaymentMethod';

/**
 * Shows the ACTIVITY MAP home's header switch (task list ⇄ map), and so the map view (the octopus) at all. Off =
 * the task list alone. A Conductor gradual feature release, ONE id for desktop web, mobile web, iOS and Android.
 */
export const ACTIVITY_MAP_VIEW_SPEC = 'symphonyHomeActivityMapView';

/**
 * The Notifications page's "What's new" tab. On = a Notifications / What's new tab row, the product-update
 * list behind the second tab, and `?tab=whats-new` opens it. Off (and while the gate is loading) = the
 * Notifications page exactly as it was: no tab row, no `/api/whats-new` read, and `?tab=whats-new` ignored.
 *
 * A Conductor feature release, ONE id for web, iOS and Android, so the tab opens on every surface at once.
 */
export const WHATS_NEW_SPEC = 'symphonyWhatsNew';

/**
 * The chat FILE PANE: a file opened from the chat docks beside the conversation instead of the modal above
 * it, and refreshes live as the agent rewrites it (`lib/chat-file-pane.ts`, `lib/use-live-doc-file.ts`).
 * Control is the app as it shipped before: the modal, loaded once. Web only for now.
 *
 * A CONDUCTOR experiment id (conductor-server `conduct`, treatment = Variant B), not a Petri spec — the
 * call must match the system the experiment is authored in (see `pages/api/flags/activity-map-view.ts`).
 */
export const CHAT_FILE_PANE_SPEC = 'symphonyChatFilePaneWeb';

/**
 * The sticky "Ask {maestro}" bar on WIDE layouts: a tap opens the side chat dock with its input focused
 * (`lib/sticky-chat-tap.ts`), instead of growing the bar into the inline composer. Control is the inline
 * composer. Web only: the phones have no side dock.
 *
 * A CONDUCTOR experiment id, like `CHAT_FILE_PANE_SPEC`.
 */
export const STICKY_CHAT_SIDE_CHAT_SPEC = 'symphonyStickyChatSideChatWeb';

/**
 * The band connect walk in a small sign-in POPUP, with the app dimmed until it closes
 * (`lib/connect-walk-popup.ts`), instead of a new tab. Control is the tab. Web only.
 *
 * A CONDUCTOR experiment id, like `CHAT_FILE_PANE_SPEC`.
 */
export const CONNECT_WALK_POPUP_SPEC = 'symphonyConnectWalkPopupWeb';

/**
 * The new desktop sidebar (Monday 13383703278): the floating rail with icon nav rows, the Spaces list,
 * the pinned chats and a collapsed icon strip (`components/symphony/sidebar/rail/`). Control is the rail
 * as it ships today. Web desktop only: the mobile drawer and the phones have their own designs.
 *
 * A CONDUCTOR experiment id, like `CHAT_FILE_PANE_SPEC`.
 */
export const NEW_SIDEBAR_SPEC = 'symphonyNewSidebarWeb';

/**
 * The Calendar: the `/calendar` screen, the Calendar card on each Space and the Calendar tile on the home.
 * A Conductor release (`symphonyCalendar`, by user id) that starts at Wix employees. Off until conducted.
 */
export const CALENDAR_SPEC = 'symphonyCalendar';

/**
 * The "personal sale" limited-time welcome offer — the top banner (a 24h countdown from the recipient's
 * createdDate) plus the "30% OFF" badge on the drawer credits card. Variant (`true`) shows them to a NEW
 * FREE-plan account while inside its 24h window; control is the app without them. Off until conducted, so
 * nobody outside the ramp sees them, and it shows nothing for a paid account or one past its window — the
 * plan and the date are the other halves of the gate and cannot be forced from here (`use-personal-sale.ts`).
 *
 * This is the SAME spec the phones conduct: web reads it on every surface, so a picker open reads the FT
 * the account was ramped on regardless of the surface it came from.
 */
export const PERSONAL_SALE_MOBILE_SPEC = 'symphonyPersonalSaleMobile';

/**
 * The Meetings channel — her joining the account's own video calls. Variant (`true`) shows the
 * Meetings section on the Channels tab and its node on the hero diagram; control shows neither.
 *
 * **Forcing this On does NOT get you a working card.** The three `/api/meetings/*` proxies conduct the
 * same spec server-side and answer 501 to an account outside the release, so an override reveals the
 * section and nothing behind it. That split is deliberate: this feature's Connect button writes a
 * connection row that lets a bot into somebody's meetings, which is not a thing a browser-side
 * override may grant.
 *
 * Bare camelCase with no `…Web` suffix: the phones have no Meetings section yet (`meetings` is absent
 * from both native `ChannelSlug` enums), and when they get one they conduct this same spec so the
 * three surfaces ramp together.
 */
export const MEETINGS_AGENT_SPEC = 'symphonyMeetingsAgent';

/**
 * Alfred on the Connectors › Outreach tab — `alfredInSymphonyOutreach`. On = the coming-soon Phone row is
 * gone and Alfred (the AI phone receptionist) sits above iMessage, with Connect / a Connected badge and
 * Manage. Off = today's list, Phone row and vote included.
 *
 * **Forcing this On draws nothing by itself.** The row is built from the server's Alfred connector entry,
 * which exists only for an account inside `alfredSymphonyConnector` (the server's own gate) and whose
 * status read succeeded. Without one there is no Alfred row AND no Phone row — the tab never offers a
 * Connect over a receptionist it cannot see. Bare camelCase, no `…Web` suffix: the phones conduct this
 * same spec, so the three surfaces ramp together.
 */
export const ALFRED_IN_OUTREACH_SPEC = 'alfredInSymphonyOutreach';

/**
 * Alfred's onboarding — `alfredOnboardingInSymphony`. On = the Outreach Alfred row's Connect opens the two-step
 * setup (`AlfredSetupModal`: "Get my number", then how to forward to it). Off = Connect is the chat handoff
 * it was before. Rides on the row, so it does nothing while `alfredInSymphonyOutreach` hides it.
 *
 * **Forcing this On cannot buy a number by itself.** The setup endpoints conduct the same toggle on the
 * caller, and `alfredSymphonyConnector` with it, and refuse when either is closed. Bare camelCase, no `…Web`
 * suffix: the phones conduct this same spec.
 */
export const ALFRED_ONBOARDING_SPEC = 'alfredOnboardingInSymphony';

/**
 * Alfred's settings screen — `alfredSettingsInSymphony`. On = Manage on the connected Outreach Alfred row opens
 * `/alfred` (number, call forwarding, and how Alfred sounds, each change handed to chat). Off = Manage opens
 * chat, as before. Rides on the row, so it does nothing while `alfredInSymphonyOutreach` hides it.
 *
 * **Forcing this On shows nothing the server would not.** The settings read conducts the same toggle on the
 * caller, and `alfredSymphonyConnector` with it, and the page sends a refused read back to the row. Bare
 * camelCase, no `…Web` suffix: the phones conduct this same spec.
 */
export const ALFRED_SETTINGS_SPEC = 'alfredSettingsInSymphony';

/**
 * The Outreach screen's Calls tab — `symphonyOutreachCallTab`. On = a third tab after Inbox: Alfred's Connect
 * while it is not set up, its number once it is. Off = the two tabs every user has. Opened to the same
 * audience as `alfredOnboardingInSymphony`, whose server gate the tab's number read sits behind. Bare
 * camelCase, no `…Web` suffix: the phones conduct this same spec.
 */
export const OUTREACH_CALL_TAB_SPEC = 'symphonyOutreachCallTab';

/**
 * Symphony Profile Links — `symphonyProfileLinks` (docs/symphony-profile-links-design.md). On = the
 * profile-link surfaces (the `/me/<handle>` page, the owner's editor, vanity handles); Off = the app exactly
 * as it was. Employees-only until the design's external gates clear.
 *
 * A Conductor gradual feature release (strategy METASITE_ID, variant B = on), the SAME id the phones, the
 * harness and the VMR services conduct, seeded by the OWNER's metasite so one account draws one arm
 * everywhere. **Forcing it On here opens nothing the server would not**: every write behind it (claiming a
 * handle, publishing a link) conducts the same release on the caller's own metasite.
 */
export const PROFILE_LINKS_SPEC = 'symphonyProfileLinks';

/**
 * The Black Friday 2026 campaign — `specs.symphony.BlackFridayCampaign`. The band, the one-time modal
 * and the plan card's "BFCM Unlimited Credits" title all hang off it.
 *
 * Fully qualified, like the other `specs.*` entries: this is the key Petri actually holds, and it is
 * the SAME one the harness conducts for the maestro's Black Friday skill and both phones conduct for
 * their own band — so the agent, the desktop and the phones ramp together.
 */
export const BLACK_FRIDAY_CAMPAIGN_SPEC = 'specs.symphony.BlackFridayCampaign';

/**
 * FullStory session recording (`lib/fullstory.ts`): On = this session is recorded through wix-recorder, org
 * 11CC00, with passwords masked. A Conductor feature toggle, by user id, ONE id for web, iOS and Android, so
 * a person is recorded on every surface or on none. Off until conducted.
 */
export const FULLSTORY_RECORDING_SPEC = 'symphonyFullStoryRecording';

export const EMAIL_PREVIEW_WIDGET_SPEC = 'symphonyEmailPreviewWidget';

export const BLOG_DRAFT_PREVIEW_SPEC = 'symphonyBlogDraftPreview';

export const SOCIAL_POST_DRAFTS_SPEC = 'symphonySocialPostDrafts';

export const MARKETING_PUBLISH_TIME_SPEC = 'symphonyMarketingPublishTime';

/**
 * Symphony group chat — `symphonyGroupChat` (docs/2026-10-30-symphony-group-chat-design-and-implementation.md §20).
 * ONE Conductor feature toggle for the whole feature, employees-only, seeded on the caller's OWN Symphony metasite
 * (METASITE_ID, variant B = on). The phones read the same id from the native-data batch, and symphony-bff conducts
 * it before any group write, so forcing it On here only reveals the group look on chats that already carry a
 * group summary — it creates or opens nothing the server would not.
 */
export const GROUP_CHAT_SPEC = 'symphonyGroupChat';

/**
 * The new Space card (Space rules spec, 2026-10-29) — `symphonySpaceCardV2`. ONE Conductor feature toggle that web, iOS
 * and Android all conduct on the signed-in USER (USER_ID, variant B = on), so the three surfaces ramp together. Keep it
 * on USER_ID: a metasite- or BiProfile-filtered group never resolves on the phones' native-data path and reads
 * permanently off there (the release's own note). On = the Space card (`lib/space-card.ts`); Off = today's site strip.
 */
export const SPACE_CARD_SPEC = 'symphonySpaceCardV2';

/**
 * Every toggle this app conducts. Add one ONLY when something actually reads it — same rule as
 * native's `ExperimentSpecs.startup`: a spec nobody reads is noise in that experiment's analysis.
 */
export const WEB_TOGGLES: WebToggle[] = [
  {
    spec: INSTANCE_AUTH_SPEC,
    description: 'Signed-instance auth for the data layer (off = the legacy credentialed fetch).',
  },
  {
    spec: VOICE_CALL_SPEC,
    description: 'Live voice calls. Voice is ON by default; set this release to control to turn it off.',
  },
  {
    spec: CREDIT_MINER_SPEC,
    description: 'The Credit Miner game inside the image-render placeholder. Off unless the server gate or this override opens it.',
  },
  {
    spec: EARN_CREDITS_OFFER_SPEC,
    description:
      'The "Earn free credits" offer at the out-of-credits moments (rail credits card, paused routine, buy-credits). Off until conducted.',
  },
  {
    spec: BILLING_CYCLE_STEP_SPEC,
    description:
      'Package picker A/B: On = no Monthly/Yearly toggle, the cycle is chosen on a second screen after the plan. Off = today’s one-screen picker.',
  },
  {
    spec: DAILY_LIMIT_BAR_SPEC,
    description:
      'The daily-limit bar on the credits surfaces (today’s allowance, beside the period meter). Shows nothing unless the account is actually under a per-day ceiling — the numbers come from the server and cannot be forced.',
  },
  {
    spec: CHANGE_PAYMENT_METHOD_SPEC,
    description:
      'The “Change payment method” item on the current-plan card’s ⋯ menu: mint a Stripe SetupIntent, confirm a new card in the browser, make it the subscription’s default. Shows nothing on an Apple-bought plan — Apple owns that card and the server refuses it.',
  },
  {
    spec: CALENDAR_SPEC,
    description:
      'Calendar: On = the Calendar screen, a Calendar card on each Space and a Calendar tile on the home. Off = none of them.',
  },
  {
    spec: FULLSTORY_RECORDING_SPEC,
    description:
      'FullStory session recording: On = this session is recorded in FullStory (production builds only; passwords masked). Takes effect on the next page load. Off = nothing is loaded or recorded.',
  },
  {
    spec: CHAT_FILE_PANE_SPEC,
    description:
      'Chat file pane: On = a file opened from the chat opens beside it (wide layouts) and updates live while the agent edits it. Off = the file opens in the modal above the chat, as before.',
  },
  {
    spec: STICKY_CHAT_SIDE_CHAT_SPEC,
    description:
      'Sticky chat opens the side chat: On = on wide screens, a tap on the "Ask Maestro" bar opens the chat on the side with its input focused. Off = the bar grows into the inline composer, as before.',
  },
  {
    spec: NEW_SIDEBAR_SPEC,
    description:
      'New desktop sidebar: On = the floating rail from the new design (icon nav, the Spaces list, pinned chats, the collapsed icon strip). Off = the sidebar as it ships today. Desktop only; the mobile drawer is unchanged.',
  },
  {
    spec: CONNECT_WALK_POPUP_SPEC,
    description:
      'Connect walk popup: On = "Connect all" on a band opens the sign-in walk in a small popup and dims the app until it closes. Off = the walk opens in a new tab, as before.',
  },
  {
    spec: ACTIVITY_MAP_VIEW_SPEC,
    description:
      'Activity Map view: On = the Overview header has its task-list / map switch, so the activity map (the octopus) can be opened, on the desktop and mobile web. Off = no switch: the home is the task list only. Only visible with the Activity Map home on.',
  },
  {
    spec: WHATS_NEW_SPEC,
    description:
      'What\'s new: On = the Notifications page has a Notifications / What\'s new tab row, and the second tab lists product updates. Off = the Notifications page as it was, with no tabs and no What\'s new read.',
  },
  {
    spec: MEETINGS_AGENT_SPEC,
    description:
      'The Meetings channel: On = the Meetings section on Channels (connect her to your video calls, and the week’s meetings). Off = no section and no hero node. The /api/meetings routes conduct this server-side too, so forcing it On shows the card and still gets 501s unless the account is really in the release.',
  },
  {
    spec: PERSONAL_SALE_MOBILE_SPEC,
    description:
      'Personal sale: On = the limited-time welcome banner (24h countdown) + the "30% OFF" badge on the credits card. Off = neither. Shows nothing unless the account is Free AND still inside its 24h window — the plan and the recipient date are the other half of the gate. Use the personal-sale preview to see it regardless. (The phones conduct this same spec.)',
  },
  {
    spec: ALFRED_IN_OUTREACH_SPEC,
    description: 'Outreach tab: the Phone row becomes an Alfred row (needs the server\'s alfredSymphonyConnector to list Alfred).',
  },
  {
    spec: ALFRED_ONBOARDING_SPEC,
    description:
      'Alfred onboarding: On = the Outreach Alfred row\'s Connect opens the two-step setup (number, then call forwarding). Off = Connect opens chat. The setup endpoints check the same toggle on the server.',
  },
  {
    spec: ALFRED_SETTINGS_SPEC,
    description:
      'Alfred settings: On = the Outreach Alfred row\'s Manage opens the /alfred settings page (number, call forwarding, how Alfred sounds). Off = Manage opens chat. The settings read checks the same toggle on the server.',
  },
  {
    spec: PROFILE_LINKS_SPEC,
    description:
      'Profile links: On = the "Contact me on Symphony" profile-link surfaces and vanity handles. Off = the app as it was. The server conducts the same release for every write, so forcing it On only reveals the screens.',
  },
  {
    spec: OUTREACH_CALL_TAB_SPEC,
    description:
      'Outreach Calls tab: On = a third tab on the Outreach screen with Alfred\'s Connect, or its number once connected. Off = Contacts and Inbox only. The number needs alfredOnboardingInSymphony on the server.',
  },
  {
    spec: EMAIL_PREVIEW_WIDGET_SPEC,
    description:
      'Email campaign review: On = a held email campaign send shows "The email, as it will be sent" with Preview and comment, which opens the email beside the chat to comment on it and send the comments as Edit first. Off = the Send it / Edit first / Drop it chips alone. The harness conducts the same release before it attaches the card.',
  },
  {
    spec: BLOG_DRAFT_PREVIEW_SPEC,
    description:
      'Blog draft preview: On = a reply that wrote a blog draft post shows "Blog post draft" with Read the post, which opens the post rendered beside the chat. Off = the reply\'s text alone. The harness conducts the same release before it attaches the card.',
  },
  {
    spec: SOCIAL_POST_DRAFTS_SPEC,
    description:
      'Social post drafts: On = a reply that saved a social post draft shows "Social post draft" with the post as it will look and Post now, Edit first and Drop, in the chat and in the task view. Off = the reply\'s text alone. The harness conducts the same toggle before it saves a social draft or attaches the card.',
  },
  {
    spec: MARKETING_PUBLISH_TIME_SPEC,
    description:
      'Marketing publish time: On = a marketing plan item opened from the Calendar shows its idea with Generate draft, Drafting…, and its social post card in every state, in the task view. Off = today\'s task view. The harness conducts the same toggle before it stores a plan row\'s publish time.',
  },
  {
    spec: GROUP_CHAT_SPEC,
    description:
      'Group chat: On = a chat shared with other people looks like a group — stacked avatars, a people icon and "Dana: …" previews in every chat list, and a group header, author avatars and join/leave lines inside the chat. Off = every chat looks as it does today. Draws nothing on a private chat; symphony-bff conducts the same toggle before any group write.',
  },
  {
    spec: BLACK_FRIDAY_CAMPAIGN_SPEC,
    description:
      'Black Friday 2026: On = the campaign band at the top of every screen, the one-time offer modal, and "BFCM Unlimited Credits" on the plan card while the free hours run. Off = none of it. The band shows during onboarding too; only the modal waits for it. Use Dev Tools ▸ Black Friday to put this browser at a point on the 48-hour clock — the band alone shows the offer, since a real window needs a real redemption. (The harness and both phones conduct this same spec.)',
  },
  {
    spec: SPACE_CARD_SPEC,
    description:
      'Space card: On = the new card at the top of a Space — View site / Edit site as equal buttons, an "Online setup" link instead of the ⋯, a Visits panel, no Wix plan row, a quiet "connect Instagram, Facebook or Google Business" footer, the Shopify / WordPress / Personal variants, and "Upgrade to Premium" opening Wix\'s pricing page. Off = today\'s site strip.',
  },
];

const PREFIX = 'orion-web:dev-ft:';
const key = (spec: string): string => `${PREFIX}${spec}`;

function store(persist: boolean): Storage | undefined {
  try {
    if (typeof window === 'undefined') return undefined;
    return persist ? window.localStorage : window.sessionStorage;
  } catch {
    return undefined; // storage blocked — no overrides, the conducted value stands
  }
}

function read(persist: boolean, spec: string): ToggleOverride | undefined {
  try {
    const value = store(persist)?.getItem(key(spec));
    return value === 'true' || value === 'false' ? value : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The forced value for a spec, or `undefined` for "use the conducted value". Persisted overrides win
 * over session ones (a Persist=on write clears the session copy, so both are never set at once).
 *
 * Safe to call from anywhere, including the flag read-path: without a dev-tools grant it is always
 * `undefined`, so this cannot change what a real user's app does.
 */
export function toggleOverride(spec: string): ToggleOverride | undefined {
  if (!devToolsUnlocked()) return undefined;
  return read(true, spec) ?? read(false, spec);
}

/** Whether this spec's override is flagged to survive a reload (native: `isOverridePersisted`). */
export function isOverridePersisted(spec: string): boolean {
  if (!devToolsUnlocked()) return false;
  return read(true, spec) !== undefined;
}

/**
 * Set or clear an override (native: `setDevOverride`).
 *
 * - `value` `null` → back to Default: dropped from BOTH stores.
 * - otherwise → written to the store `persist` selects, and removed from the other, so flipping Persist
 *   moves the override rather than leaving a stale twin behind.
 */
export function setToggleOverride(spec: string, value: ToggleOverride | null, persist: boolean): void {
  if (!devToolsUnlocked()) return;
  try {
    if (value === null) {
      store(true)?.removeItem(key(spec));
      store(false)?.removeItem(key(spec));
      return;
    }
    store(persist)?.setItem(key(spec), value);
    store(!persist)?.removeItem(key(spec));
  } catch {
    /* quota / blocked storage — the panel simply shows no override */
  }
}

/** Drop every override (the panel's "Use conducted values"). */
export function clearToggleOverrides(): void {
  if (!devToolsUnlocked()) return;
  for (const { spec } of WEB_TOGGLES) setToggleOverride(spec, null, true);
}

/** How many toggles are currently forced — what the dev menu badges itself with. */
export function overriddenToggleCount(): number {
  return WEB_TOGGLES.filter(({ spec }) => toggleOverride(spec) !== undefined).length;
}
